import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { TripIconLockDialogComponent } from './trip-icon-lock-dialog.component';
import { MenuStateService } from '../menu-state.service';
import { APP_ROLE_OPTIONS, maskIncludesRole } from '../app-roles';
import { MENU_ICON_OPTIONS, MenuIconOption } from './menu-icon-options';

type MenuKind = 'menu' | 'subMenu' | 'subSubMenu';
type EditorMode = 'create' | 'edit';
type DiagnosticTone = 'good' | 'warning' | 'danger' | 'neutral';
type ProductionStatusCode = 'in_production' | 'missing' | 'different' | 'duplicate_route' | 'prod_conflict';
type ProductionSummaryKey = keyof ProductionCompareSummary;

interface MenuDiagnostic {
  label: string;
  tone: DiagnosticTone;
  icon: string;
}

interface MenuSelection {
  kind: MenuKind;
  id: number;
}

interface MenuItem {
  ams_side_menu_id: number;
  ams_side_menu_text: string;
  ams_side_menu_order: number | null;
  ams_side_menu_description: string | null;
  access_roles: number;
  children: SubMenuItem[];
}

interface SubMenuItem {
  ams_side_sub_menu_id: number;
  ams_side_menu_id: number | null;
  ams_side_sub_menu_text: string;
  ams_side_sub_menu_page: string | null;
  ams_side_sub_menu_icon: string | null;
  ams_side_sub_menu_order: number | null;
  ams_side_sub_sub_menu: boolean;
  ams_side_sub_menu_description: string | null;
  access_roles: number;
  menu_parent: string | null;
  child_count: number;
  children: SubSubMenuItem[];
}

interface SubSubMenuItem {
  ams_side_sub_sub_menu_id: number;
  ams_side_sub_menu_id: number | null;
  ams_side_sub_sub_menu_text: string;
  ams_side_sub_sub_menu_page: string | null;
  ams_side_sub_sub_menu_icon: string | null;
  ams_side_sub_sub_menu_order: number | null;
  ams_side_sub_sub_menu_description: string | null;
  access_roles: number;
  sub_menu_parent: string | null;
}

interface MenuTreeResponse {
  menus: MenuItem[];
  sub_menus: SubMenuItem[];
  sub_sub_menus: SubSubMenuItem[];
  tree: MenuItem[];
}

interface MenuSaveResponse {
  message: string;
  item?: Record<string, unknown>;
  tree?: MenuTreeResponse;
}

interface ProductionItemStatus {
  status: ProductionStatusCode;
  label: string;
  tone: DiagnosticTone;
  icon: string;
  diffs?: string[];
  prod_id?: number | null;
  prod_label?: string | null;
}

interface ProductionOnlyItem {
  prod_id: number;
  label: string;
  route?: string | null;
}

interface ProductionCompareSummary {
  dev_total: number;
  prod_total: number;
  in_production: number;
  missing: number;
  different: number;
  duplicate_route: number;
  prod_conflict: number;
  prod_only: number;
}

interface ProductionCompareResponse {
  available: boolean;
  source?: string;
  message?: string;
  summary?: Partial<ProductionCompareSummary>;
  statuses?: Partial<Record<MenuKind, Record<string, ProductionItemStatus>>>;
  prod_only?: Partial<Record<MenuKind, ProductionOnlyItem[]>>;
}

interface ProductionPromoteResponse {
  message: string;
  production?: ProductionCompareResponse;
}

@Component({
  selector: 'app-menu-manager',
  templateUrl: './menu-manager.component.html',
  styleUrls: ['./menu-manager.component.scss']
})
export class MenuManagerComponent implements OnInit {
  private readonly menuManagerUrl = '/api/menu_manager/menu_manager.php';

  menus: MenuItem[] = [];
  subMenus: SubMenuItem[] = [];
  subSubMenus: SubSubMenuItem[] = [];
  filterText = '';
  loading = false;
  saving = false;
  reordering = false;
  errorMessage = '';
  successMessage = '';
  productionCompare: ProductionCompareResponse | null = null;
  productionLoading = false;
  productionError = '';
  promoting = false;
  themeMode: 'light' | 'dark' = 'light';
  editorMode: EditorMode = 'edit';
  selectedKind: MenuKind = 'menu';
  selectedId: number | null = null;

  private tripIconDialogOpen = false;
  iconPickerOpen = false;
  iconSearch = '';
  iconStyleFilter = 'all';
  iconResultLimit = 72;

  readonly accessRoleOptions = APP_ROLE_OPTIONS;
  detailForm: FormGroup;

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private menuStateService: MenuStateService,
    private dialog: MatDialog,
  ) {
    this.detailForm = this.fb.group({
      ams_side_menu_id: [null],
      ams_side_menu_text: [''],
      ams_side_menu_order: [null],
      ams_side_menu_description: [''],
      ams_side_sub_menu_id: [null],
      ams_side_sub_menu_text: [''],
      ams_side_sub_menu_page: [''],
      ams_side_sub_menu_icon: [''],
      ams_side_sub_menu_order: [null],
      ams_side_sub_sub_menu: [false],
      ams_side_sub_menu_description: [''],
      ams_side_sub_sub_menu_id: [null],
      ams_side_sub_sub_menu_text: [''],
      ams_side_sub_sub_menu_page: [''],
      ams_side_sub_sub_menu_icon: [''],
      ams_side_sub_sub_menu_order: [null],
      ams_side_sub_sub_menu_description: [''],
      access_roles: [0],
    });
  }

  ngOnInit(): void {
    this.menuStateService.themeState$.subscribe((theme) => {
      this.themeMode = theme === 'ams-content-dark' ? 'dark' : 'light';
    });

    this.loadMenuData();
  }

  get filteredMenus(): MenuItem[] {
    const search = this.filterText.trim().toLowerCase();

    if (!search) {
      return this.menus;
    }

    return this.menus
      .map((menu) => {
        const menuMatches = this.menuHaystack(menu).includes(search);

        if (menuMatches) {
          return menu;
        }

        const children = menu.children
          .map((subMenu) => {
            const subMenuMatches = this.subMenuHaystack(subMenu).includes(search);

            if (subMenuMatches) {
              return subMenu;
            }

            const nestedChildren = subMenu.children.filter((nested) => this.subSubMenuHaystack(nested).includes(search));
            return nestedChildren.length ? { ...subMenu, children: nestedChildren } : null;
          })
          .filter((item): item is SubMenuItem => item !== null);

        return children.length ? { ...menu, children } : null;
      })
      .filter((item): item is MenuItem => item !== null);
  }

  get selectedItem(): MenuItem | SubMenuItem | SubSubMenuItem | null {
    if (this.selectedId === null) {
      return null;
    }

    return this.findItem(this.selectedKind, this.selectedId);
  }

  get selectedKindLabel(): string {
    if (this.selectedKind === 'menu') {
      return 'Menu';
    }

    if (this.selectedKind === 'subMenu') {
      return 'Sub Menu';
    }

    return 'Nested Menu';
  }

  get selectedTitle(): string {
    if (this.editorMode === 'create') {
      return `New ${this.selectedKindLabel}`;
    }

    const item = this.selectedItem;
    return item ? this.itemLabel(this.selectedKind, item) : 'Select an item';
  }

  get selectedDiagnostics(): MenuDiagnostic[] {
    const item = this.editorMode === 'edit' ? this.selectedItem : null;

    if (!item) {
      return [
        { label: 'Auto position', tone: 'good', icon: 'fa-circle-check' },
        { label: 'Draft', tone: 'neutral', icon: 'fa-pen' },
      ];
    }

    const diagnostics = this.diagnosticsFor(this.selectedKind, item);
    const productionDiagnostic = this.productionDiagnosticFor(this.selectedKind, item);

    return productionDiagnostic ? [productionDiagnostic, ...diagnostics] : diagnostics;
  }

  get selectedProductionStatus(): ProductionItemStatus | null {
    const item = this.editorMode === 'edit' ? this.selectedItem : null;
    return item ? this.productionStatusFor(this.selectedKind, item) : null;
  }

  get selectedProductionDiffs(): string[] {
    return this.selectedProductionStatus?.diffs ?? [];
  }

  get issueCount(): number {
    return this.menus.reduce((count, menu) => {
      count += this.warningDiagnosticsFor('menu', menu).length;
      menu.children.forEach((subMenu) => {
        count += this.warningDiagnosticsFor('subMenu', subMenu).length;
        subMenu.children.forEach((nested) => {
          count += this.warningDiagnosticsFor('subSubMenu', nested).length;
        });
      });
      return count;
    }, 0);
  }

  get visibleItemCount(): number {
    return this.filteredMenus.reduce((count, menu) => {
      count += 1;
      menu.children.forEach((subMenu) => {
        count += 1 + subMenu.children.length;
      });
      return count;
    }, 0);
  }

  get productionAttentionCount(): number {
    return this.productionSummaryValue('missing')
      + this.productionSummaryValue('different')
      + this.productionSummaryValue('duplicate_route')
      + this.productionSummaryValue('prod_conflict');
  }

  get productionHealthy(): boolean {
    return Boolean(this.productionCompare?.available)
      && !this.productionLoading
      && !this.productionError
      && this.productionAttentionCount === 0;
  }

  get productionPillIcon(): string {
    if (this.productionLoading) {
      return 'fa-circle-notch fa-spin';
    }

    if (this.productionError || this.productionCompare?.available === false) {
      return 'fa-plug-circle-xmark';
    }

    if (this.productionAttentionCount > 0) {
      return 'fa-cloud-arrow-up';
    }

    if (this.productionSummaryValue('prod_only') > 0) {
      return 'fa-code-compare';
    }

    return 'fa-circle-check';
  }

  get productionPillLabel(): string {
    if (this.productionLoading) {
      return 'Checking prod';
    }

    if (this.productionError || this.productionCompare?.available === false) {
      return 'Prod offline';
    }

    if (!this.productionCompare) {
      return 'Prod unchecked';
    }

    if (this.productionAttentionCount > 0) {
      return `${this.productionAttentionCount} Prod gaps`;
    }

    const prodOnly = this.productionSummaryValue('prod_only');
    if (prodOnly > 0) {
      return `${prodOnly} Prod-only`;
    }

    return 'Prod synced';
  }

  get productionPillTitle(): string {
    if (this.productionError) {
      return this.productionError;
    }

    if (!this.productionCompare) {
      return 'Production status has not been checked yet.';
    }

    return `${this.productionSummaryValue('in_production')} live, ${this.productionSummaryValue('missing')} missing, ${this.productionSummaryValue('different')} different, ${this.productionSummaryValue('prod_conflict')} conflicts.`;
  }

  get canPromoteSelected(): boolean {
    return this.editorMode === 'edit'
      && this.selectedId !== null
      && this.selectedProductionStatus?.status === 'missing'
      && !this.promoting;
  }

  get promoteButtonTitle(): string {
    if (this.promoting) {
      return 'Sending selected item to production.';
    }

    if (this.selectedProductionStatus?.status === 'missing') {
      return 'Send this missing menu item to production.';
    }

    return 'Only items marked Missing prod can be sent to production.';
  }

  get tripIconLocked(): boolean {
    if (this.editorMode !== 'edit' || this.selectedKind !== 'subMenu') return false;
    const item = this.selectedItem as SubMenuItem | null;
    const route = String(item?.ams_side_sub_menu_page ?? '').split(/[?#]/)[0].replace(/^\/+|\/+$/g, '').toLowerCase();
    return Number(item?.ams_side_sub_menu_id) === 6 || route === 'trips';
  }

  toggleIconPicker(): void {
    if (this.tripIconLocked) { this.showTripIconLock(); return; }
    this.iconPickerOpen = !this.iconPickerOpen;
  }

  onIconFieldAttempt(event: Event): void {
    if (!this.tripIconLocked) return;
    event.preventDefault();
    this.showTripIconLock();
  }

  onLockedIconKeydown(event: KeyboardEvent): void {
    if (!this.tripIconLocked) return;
    if (['Tab', 'Shift', 'Control', 'Meta', 'Alt', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    if ((event.ctrlKey || event.metaKey) && ['a', 'c'].includes(event.key.toLowerCase())) return;
    event.preventDefault();
    this.showTripIconLock();
  }

  private showTripIconLock(): void {
    this.iconPickerOpen = false;
    if (this.tripIconDialogOpen) return;
    this.tripIconDialogOpen = true;
    const ref = this.dialog.open(TripIconLockDialogComponent, {
      disableClose: true,
      autoFocus: 'dialog',
      restoreFocus: true,
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      ariaLabel: 'Leave the Trip Icon Alone',
      ariaDescribedBy: 'lk-apology-label',
    });
    ref.afterClosed().subscribe(() => { this.tripIconDialogOpen = false; });
  }

  get selectedIconClass(): string {
    const field = this.selectedKind === 'subMenu' ? 'ams_side_sub_menu_icon' : 'ams_side_sub_sub_menu_icon';
    return this.selectedKind === 'menu' ? '' : this.cleanText(this.detailForm.get(field)?.value);
  }

  get matchingIcons(): MenuIconOption[] {
    const terms = this.iconSearch.toLowerCase().trim().replace(/fa-/g, '').split(/[\s-]+/).filter(Boolean);
    return MENU_ICON_OPTIONS.filter((icon) =>
      (this.iconStyleFilter === 'all' || icon.style === this.iconStyleFilter)
      && terms.every((term) => `${icon.search} ${icon.style}`.includes(term))
    );
  }

  get visibleIcons(): MenuIconOption[] {
    return this.matchingIcons.slice(0, this.iconResultLimit);
  }

  chooseIcon(className: string): void {
    if (this.selectedKind === 'menu') return;
    if (this.tripIconLocked) { this.showTripIconLock(); return; }
    const field = this.selectedKind === 'subMenu' ? 'ams_side_sub_menu_icon' : 'ams_side_sub_sub_menu_icon';
    const control = this.detailForm.get(field);
    control?.setValue(className);
    control?.markAsDirty();
    this.iconPickerOpen = false;
  }

  trackIcon(_index: number, icon: MenuIconOption): string {
    return icon.className;
  }

  private resetIconPicker(): void {
    this.iconPickerOpen = false;
    this.iconSearch = '';
    this.iconStyleFilter = 'all';
    this.iconResultLimit = 72;
  }

  get selectedAccessRoleValues(): number[] {
    const mask = Number(this.detailForm.get('access_roles')?.value ?? 0);
    return this.accessRoleOptions.filter((role) => maskIncludesRole(mask, role.value)).map((role) => role.value);
  }

  get unmappedAccessMask(): number {
    const mask = Number(this.detailForm.get('access_roles')?.value ?? 0);
    return mask - this.selectedAccessRoleValues.reduce((total, value) => total + value, 0);
  }

  setAccessRoles(values: number[]): void {
    // Preserve bits without a named role. Arithmetic also supports roles above 32 bits.
    const selected = new Set(values);
    const mask = this.unmappedAccessMask + this.accessRoleOptions
      .filter((role) => selected.has(role.value))
      .reduce((total, role) => total + role.value, 0);
    const control = this.detailForm.get('access_roles');
    control?.setValue(mask);
    control?.markAsDirty();
  }

  get parentSubMenuOptions(): SubMenuItem[] {
    return this.subMenus;
  }

  get canAddSubMenu(): boolean {
    return this.menus.length > 0;
  }

  get canAddSubSubMenu(): boolean {
    return this.subMenus.length > 0;
  }

  loadMenuData(preferredSelection?: MenuSelection): void {
    this.loading = true;
    this.errorMessage = '';

    this.http.get<MenuTreeResponse>(this.menuManagerUrl, { params: { type: 'tree' } }).subscribe({
      next: (response) => {
        this.applyTreeResponse(response, preferredSelection);
        this.loading = false;
        this.loadProductionStatus();
      },
      error: (error) => {
        this.loading = false;
        this.errorMessage = error?.error?.message || 'Unable to load menu data.';
      }
    });
  }

  refreshData(): void {
    const selection = this.currentSelection();
    this.loadMenuData(selection ?? undefined);
  }

  refreshProductionStatus(): void {
    this.loadProductionStatus(true);
  }

  promoteSelectedToProduction(): void {
    if (!this.canPromoteSelected || this.selectedId === null) {
      return;
    }

    const item = this.selectedItem;
    const label = item ? this.itemLabel(this.selectedKind, item) : 'this menu item';
    if (!confirm(`Send ${label} to production?`)) {
      return;
    }

    this.promoting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post<ProductionPromoteResponse>(this.menuManagerUrl, {
      action: 'promoteToProduction',
      kind: this.selectedKind,
      id: this.selectedId,
    }).subscribe({
      next: (response) => {
        this.promoting = false;
        if (response.production) {
          this.productionCompare = response.production;
        }
        else {
          this.loadProductionStatus();
        }
        this.successMessage = response?.message || 'Menu item sent to production.';
        this.toastr.success(this.successMessage);
      },
      error: (error) => {
        this.promoting = false;
        this.errorMessage = error?.error?.message || 'Unable to send menu item to production.';
        this.toastr.error(this.errorMessage);
      }
    });
  }

  productionStatusFor(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): ProductionItemStatus | null {
    const id = this.idForKind(kind, item);

    if (id === null) {
      return null;
    }

    return this.productionCompare?.statuses?.[kind]?.[String(id)] ?? null;
  }

  productionDiagnosticFor(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): MenuDiagnostic | null {
    const status = this.productionStatusFor(kind, item);

    if (!status) {
      return null;
    }

    return {
      label: status.label,
      tone: status.tone,
      icon: status.icon,
    };
  }

  productionTitleFor(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): string {
    const status = this.productionStatusFor(kind, item);

    if (!status) {
      return this.productionError || 'Production status not checked.';
    }

    const diffs = status.diffs?.length ? `: ${status.diffs.join(', ')}` : '';
    return `${status.label}${diffs}`;
  }

  loadProductionStatus(showToast = false): void {
    if (this.productionLoading) {
      return;
    }

    this.productionLoading = true;
    this.productionError = '';

    this.http.get<ProductionCompareResponse>(this.menuManagerUrl, { params: { type: 'production_compare' } }).subscribe({
      next: (response) => {
        this.productionLoading = false;
        this.productionCompare = response;

        if (!response?.available) {
          this.productionError = response?.message || 'Production menu status is unavailable.';
          if (showToast) {
            this.toastr.warning(this.productionError);
          }
          return;
        }

        if (showToast) {
          this.toastr.success('Production menu status refreshed.');
        }
      },
      error: (error) => {
        this.productionLoading = false;
        this.productionError = error?.error?.message || 'Unable to check production menu status.';
        if (showToast) {
          this.toastr.error(this.productionError);
        }
      }
    });
  }

  toggleTheme(): void {
    this.menuStateService.toggleTheme();
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  selectMenu(menu: MenuItem): void {
    this.selectExisting('menu', menu.ams_side_menu_id);
  }

  selectSubMenu(subMenu: SubMenuItem): void {
    this.selectExisting('subMenu', subMenu.ams_side_sub_menu_id);
  }

  selectSubSubMenu(subSubMenu: SubSubMenuItem): void {
    this.selectExisting('subSubMenu', subSubMenu.ams_side_sub_sub_menu_id);
  }

  beginAddMenu(): void {
    this.resetIconPicker();
    this.editorMode = 'create';
    this.selectedKind = 'menu';
    this.selectedId = null;
    this.successMessage = '';
    this.errorMessage = '';
    this.detailForm.reset({
      ams_side_menu_order: null,
      ams_side_menu_description: '',
      access_roles: 0,
    });
  }

  beginAddSubMenu(parentMenu?: MenuItem): void {
    const parentId = parentMenu?.ams_side_menu_id ?? this.preferredParentMenuId();

    if (!parentId) {
      this.errorMessage = 'Add a top-level menu before adding a sub menu.';
      return;
    }

    this.resetIconPicker();
    this.editorMode = 'create';
    this.selectedKind = 'subMenu';
    this.selectedId = null;
    this.successMessage = '';
    this.errorMessage = '';
    this.detailForm.reset({
      ams_side_menu_id: parentId,
      ams_side_sub_menu_order: null,
      ams_side_sub_sub_menu: false,
      ams_side_sub_menu_description: '',
      access_roles: 0,
    });
  }

  beginAddSubSubMenu(parentSubMenu?: SubMenuItem): void {
    const parentId = parentSubMenu?.ams_side_sub_menu_id ?? this.preferredParentSubMenuId();

    if (!parentId) {
      this.errorMessage = 'Add a sub menu before adding a nested menu.';
      return;
    }

    this.resetIconPicker();
    this.editorMode = 'create';
    this.selectedKind = 'subSubMenu';
    this.selectedId = null;
    this.successMessage = '';
    this.errorMessage = '';
    this.detailForm.reset({
      ams_side_sub_menu_id: parentId,
      ams_side_sub_sub_menu_order: null,
      ams_side_sub_sub_menu_description: '',
      access_roles: 0,
    });
  }

  saveSelected(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const payload = this.buildSavePayload();
    if (!payload) {
      return;
    }

    this.saving = true;

    this.http.post<MenuSaveResponse>(this.menuManagerUrl, payload).subscribe({
      next: (response) => {
        this.saving = false;
        const savedSelection = this.selectionFromSavedItem(response.item) ?? this.currentSelection() ?? undefined;
        if (response.tree) {
          this.applyTreeResponse(response.tree, savedSelection);
          this.loadProductionStatus();
        }
        else {
          this.loadMenuData(savedSelection);
        }
        this.successMessage = response?.message || 'Menu item saved.';
        this.toastr.success(this.successMessage);
      },
      error: (error) => {
        this.saving = false;
        if (error?.error?.code === 'MENU_ICON_LOCKED') {
          this.detailForm.get('ams_side_sub_menu_icon')?.setValue('fa-solid fa-person-falling');
          this.showTripIconLock();
          return;
        }
        this.errorMessage = error?.error?.message || 'Unable to save menu item.';
        this.toastr.error(this.errorMessage);
      }
    });
  }

  resetEditor(): void {
    if (this.editorMode === 'create') {
      if (this.selectedKind === 'menu') {
        this.beginAddMenu();
      }
      else if (this.selectedKind === 'subMenu') {
        this.beginAddSubMenu();
      }
      else {
        this.beginAddSubSubMenu();
      }
      return;
    }

    if (this.selectedId !== null) {
      this.selectExisting(this.selectedKind, this.selectedId);
    }
  }

  moveMenu(menu: MenuItem, direction: -1 | 1): void {
    this.reorderItems('menu', null, this.menus, menu.ams_side_menu_id, direction);
  }

  moveSubMenu(menu: MenuItem, subMenu: SubMenuItem, direction: -1 | 1): void {
    this.reorderItems('subMenu', menu.ams_side_menu_id, menu.children, subMenu.ams_side_sub_menu_id, direction);
  }

  moveSubSubMenu(subMenu: SubMenuItem, subSubMenu: SubSubMenuItem, direction: -1 | 1): void {
    this.reorderItems('subSubMenu', subMenu.ams_side_sub_menu_id, subMenu.children, subSubMenu.ams_side_sub_sub_menu_id, direction);
  }

  canMoveMenu(menu: MenuItem, direction: -1 | 1): boolean {
    return this.canMove(this.menus, menu.ams_side_menu_id, direction, 'menu');
  }

  canMoveSubMenu(menu: MenuItem, subMenu: SubMenuItem, direction: -1 | 1): boolean {
    return this.canMove(menu.children, subMenu.ams_side_sub_menu_id, direction, 'subMenu');
  }

  canMoveSubSubMenu(subMenu: SubMenuItem, subSubMenu: SubSubMenuItem, direction: -1 | 1): boolean {
    return this.canMove(subMenu.children, subSubMenu.ams_side_sub_sub_menu_id, direction, 'subSubMenu');
  }

  isSelected(kind: MenuKind, id: number): boolean {
    return this.editorMode === 'edit' && this.selectedKind === kind && this.selectedId === id;
  }

  warningDiagnosticsFor(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): MenuDiagnostic[] {
    return this.diagnosticsFor(kind, item).filter((diagnostic) => diagnostic.tone === 'warning' || diagnostic.tone === 'danger');
  }

  diagnosticsFor(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): MenuDiagnostic[] {
    if (kind === 'menu') {
      const menu = item as MenuItem;
      const diagnostics: MenuDiagnostic[] = [];

      diagnostics.push(menu.ams_side_menu_order === null
        ? { label: 'No position', tone: 'warning', icon: 'fa-triangle-exclamation' }
        : { label: `Position ${menu.ams_side_menu_order}`, tone: 'good', icon: 'fa-arrow-down-1-9' });

      if (!menu.children.length) {
        diagnostics.push({ label: 'No sub menus', tone: 'warning', icon: 'fa-list' });
      }

      diagnostics.push(menu.access_roles > 0
        ? { label: 'Role access set', tone: 'good', icon: 'fa-shield-halved' }
        : { label: 'No role access', tone: 'warning', icon: 'fa-lock' });

      return diagnostics;
    }

    if (kind === 'subMenu') {
      const subMenu = item as SubMenuItem;
      const diagnostics: MenuDiagnostic[] = [];

      diagnostics.push(subMenu.ams_side_sub_menu_order === null
        ? { label: 'No position', tone: 'warning', icon: 'fa-triangle-exclamation' }
        : { label: `Position ${subMenu.ams_side_sub_menu_order}`, tone: 'good', icon: 'fa-arrow-down-1-9' });

      if (!subMenu.ams_side_menu_id) {
        diagnostics.push({ label: 'Missing parent', tone: 'danger', icon: 'fa-link-slash' });
      }

      if (!this.hasText(subMenu.ams_side_sub_menu_page) && !subMenu.ams_side_sub_sub_menu) {
        diagnostics.push({ label: 'No route', tone: 'danger', icon: 'fa-route' });
      }

      if (!this.hasText(subMenu.ams_side_sub_menu_icon)) {
        diagnostics.push({ label: 'No icon', tone: 'warning', icon: 'fa-icons' });
      }

      if (subMenu.child_count > 0 && !subMenu.ams_side_sub_sub_menu) {
        diagnostics.push({ label: 'Child flag off', tone: 'warning', icon: 'fa-sitemap' });
      }

      diagnostics.push(subMenu.access_roles > 0
        ? { label: 'Role access set', tone: 'good', icon: 'fa-shield-halved' }
        : { label: 'No role access', tone: 'warning', icon: 'fa-lock' });

      return diagnostics;
    }

    const subSubMenu = item as SubSubMenuItem;
    const diagnostics: MenuDiagnostic[] = [];

    diagnostics.push(subSubMenu.ams_side_sub_sub_menu_order === null
      ? { label: 'No position', tone: 'warning', icon: 'fa-triangle-exclamation' }
      : { label: `Position ${subSubMenu.ams_side_sub_sub_menu_order}`, tone: 'good', icon: 'fa-arrow-down-1-9' });

    if (!subSubMenu.ams_side_sub_menu_id) {
      diagnostics.push({ label: 'Missing parent', tone: 'danger', icon: 'fa-link-slash' });
    }

    if (!this.hasText(subSubMenu.ams_side_sub_sub_menu_page)) {
      diagnostics.push({ label: 'No route', tone: 'danger', icon: 'fa-route' });
    }

    if (!this.hasText(subSubMenu.ams_side_sub_sub_menu_icon)) {
      diagnostics.push({ label: 'No icon', tone: 'warning', icon: 'fa-icons' });
    }

    diagnostics.push(subSubMenu.access_roles > 0
      ? { label: 'Role access set', tone: 'good', icon: 'fa-shield-halved' }
      : { label: 'No role access', tone: 'warning', icon: 'fa-lock' });

    return diagnostics;
  }

  itemLabel(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): string {
    if (kind === 'menu') {
      return (item as MenuItem).ams_side_menu_text || 'Untitled menu';
    }

    if (kind === 'subMenu') {
      return (item as SubMenuItem).ams_side_sub_menu_text || 'Untitled sub menu';
    }

    return (item as SubSubMenuItem).ams_side_sub_sub_menu_text || 'Untitled nested menu';
  }

  routeLabel(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): string {
    if (kind === 'menu') {
      return 'Section';
    }

    const route = kind === 'subMenu'
      ? (item as SubMenuItem).ams_side_sub_menu_page
      : (item as SubSubMenuItem).ams_side_sub_sub_menu_page;

    return this.hasText(route) ? `/${route}` : 'No route';
  }

  iconClass(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): string {
    if (kind === 'menu') {
      return 'fa-solid fa-layer-group';
    }

    const icon = kind === 'subMenu'
      ? (item as SubMenuItem).ams_side_sub_menu_icon
      : (item as SubSubMenuItem).ams_side_sub_sub_menu_icon;

    return this.hasText(icon) ? String(icon) : 'fa-solid fa-circle-dot';
  }

  optionLabelForSubMenu(subMenu: SubMenuItem): string {
    const parent = subMenu.menu_parent ? `${subMenu.menu_parent} / ` : '';
    return `${parent}${subMenu.ams_side_sub_menu_text}`;
  }

  private applyTreeResponse(response: MenuTreeResponse, preferredSelection?: MenuSelection): void {
    this.menus = (response?.tree ?? []).map((menu) => this.normalizeMenu(menu));
    this.subMenus = (response?.sub_menus ?? []).map((subMenu) => this.normalizeSubMenu(subMenu));
    this.subSubMenus = (response?.sub_sub_menus ?? []).map((subSubMenu) => this.normalizeSubSubMenu(subSubMenu));

    if (preferredSelection && this.selectExisting(preferredSelection.kind, preferredSelection.id, false)) {
      return;
    }

    if (this.currentSelection() && this.selectExisting(this.selectedKind, this.selectedId as number, false)) {
      return;
    }

    this.selectFirstAvailable();
  }

  private selectExisting(kind: MenuKind, id: number, clearMessages = true): boolean {
    const item = this.findItem(kind, id);

    if (!item) {
      return false;
    }

    this.resetIconPicker();
    this.editorMode = 'edit';
    this.selectedKind = kind;
    this.selectedId = id;

    if (clearMessages) {
      this.errorMessage = '';
      this.successMessage = '';
    }

    this.patchFormForItem(kind, item);
    return true;
  }

  private patchFormForItem(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): void {
    this.detailForm.reset({ access_roles: item.access_roles ?? 0 });

    if (kind === 'menu') {
      const menu = item as MenuItem;
      this.detailForm.patchValue({
        ams_side_menu_id: menu.ams_side_menu_id,
        ams_side_menu_text: menu.ams_side_menu_text,
        ams_side_menu_order: menu.ams_side_menu_order,
        ams_side_menu_description: menu.ams_side_menu_description ?? '',
        access_roles: menu.access_roles,
      });
      return;
    }

    if (kind === 'subMenu') {
      const subMenu = item as SubMenuItem;
      this.detailForm.patchValue({
        ams_side_sub_menu_id: subMenu.ams_side_sub_menu_id,
        ams_side_menu_id: subMenu.ams_side_menu_id,
        ams_side_sub_menu_text: subMenu.ams_side_sub_menu_text,
        ams_side_sub_menu_page: subMenu.ams_side_sub_menu_page ?? '',
        ams_side_sub_menu_icon: subMenu.ams_side_sub_menu_icon ?? '',
        ams_side_sub_menu_order: subMenu.ams_side_sub_menu_order,
        ams_side_sub_sub_menu: subMenu.ams_side_sub_sub_menu,
        ams_side_sub_menu_description: subMenu.ams_side_sub_menu_description ?? '',
        access_roles: subMenu.access_roles,
      });
      return;
    }

    const subSubMenu = item as SubSubMenuItem;
    this.detailForm.patchValue({
      ams_side_sub_sub_menu_id: subSubMenu.ams_side_sub_sub_menu_id,
      ams_side_sub_menu_id: subSubMenu.ams_side_sub_menu_id,
      ams_side_sub_sub_menu_text: subSubMenu.ams_side_sub_sub_menu_text,
      ams_side_sub_sub_menu_page: subSubMenu.ams_side_sub_sub_menu_page ?? '',
      ams_side_sub_sub_menu_icon: subSubMenu.ams_side_sub_sub_menu_icon ?? '',
      ams_side_sub_sub_menu_order: subSubMenu.ams_side_sub_sub_menu_order,
      ams_side_sub_sub_menu_description: subSubMenu.ams_side_sub_sub_menu_description ?? '',
      access_roles: subSubMenu.access_roles,
    });
  }

  private buildSavePayload(): Record<string, unknown> | null {
    const value = this.detailForm.value;
    if (this.tripIconLocked && this.cleanText(value.ams_side_sub_menu_icon) !== 'fa-solid fa-person-falling') {
      this.detailForm.get('ams_side_sub_menu_icon')?.setValue('fa-solid fa-person-falling');
      this.showTripIconLock();
      return null;
    }
    const accessMask = Number(value.access_roles ?? 0);
    if (!Number.isSafeInteger(accessMask) || accessMask < 0) {
      this.errorMessage = 'The selected roles exceed the supported access mask for this menu level.';
      return null;
    }

    if (this.selectedKind === 'menu') {
      const text = this.cleanText(value.ams_side_menu_text);
      if (!text) {
        this.errorMessage = 'Menu text is required.';
        return null;
      }

      return {
        action: this.editorMode === 'create' ? 'addMenu' : 'updateMenu',
        ams_side_menu_id: value.ams_side_menu_id,
        ams_side_menu_text: text,
        ams_side_menu_order: this.optionalNumber(value.ams_side_menu_order),
        ams_side_menu_description: this.cleanText(value.ams_side_menu_description),
        access_roles: this.optionalNumber(value.access_roles) ?? 0,
      };
    }

    if (this.selectedKind === 'subMenu') {
      const text = this.cleanText(value.ams_side_sub_menu_text);
      const parentId = this.optionalNumber(value.ams_side_menu_id);

      if (!parentId) {
        this.errorMessage = 'Parent menu is required.';
        return null;
      }

      if (!text) {
        this.errorMessage = 'Sub-menu text is required.';
        return null;
      }

      return {
        action: this.editorMode === 'create' ? 'addSubMenu' : 'updateSubMenu',
        ams_side_sub_menu_id: value.ams_side_sub_menu_id,
        ams_side_menu_id: parentId,
        ams_side_sub_menu_text: text,
        ams_side_sub_menu_page: this.cleanText(value.ams_side_sub_menu_page),
        ams_side_sub_menu_icon: this.cleanText(value.ams_side_sub_menu_icon),
        ams_side_sub_menu_order: this.optionalNumber(value.ams_side_sub_menu_order),
        ams_side_sub_sub_menu: Boolean(value.ams_side_sub_sub_menu),
        ams_side_sub_menu_description: this.cleanText(value.ams_side_sub_menu_description),
        access_roles: this.optionalNumber(value.access_roles) ?? 0,
      };
    }

    const text = this.cleanText(value.ams_side_sub_sub_menu_text);
    const parentId = this.optionalNumber(value.ams_side_sub_menu_id);

    if (!parentId) {
      this.errorMessage = 'Parent sub menu is required.';
      return null;
    }

    if (!text) {
      this.errorMessage = 'Nested menu text is required.';
      return null;
    }

    return {
      action: this.editorMode === 'create' ? 'addSubSubMenu' : 'updateSubSubMenu',
      ams_side_sub_sub_menu_id: value.ams_side_sub_sub_menu_id,
      ams_side_sub_menu_id: parentId,
      ams_side_sub_sub_menu_text: text,
      ams_side_sub_sub_menu_page: this.cleanText(value.ams_side_sub_sub_menu_page),
      ams_side_sub_sub_menu_icon: this.cleanText(value.ams_side_sub_sub_menu_icon),
      ams_side_sub_sub_menu_order: this.optionalNumber(value.ams_side_sub_sub_menu_order),
      ams_side_sub_sub_menu_description: this.cleanText(value.ams_side_sub_sub_menu_description),
      access_roles: this.optionalNumber(value.access_roles) ?? 0,
    };
  }

  private reorderItems(kind: MenuKind, parentId: number | null, siblings: Array<MenuItem | SubMenuItem | SubSubMenuItem>, itemId: number, direction: -1 | 1): void {
    if (this.reordering) {
      return;
    }

    const ids = siblings.map((item) => this.idForKind(kind, item)).filter((id): id is number => id !== null);
    const currentIndex = ids.indexOf(itemId);
    const nextIndex = currentIndex + direction;

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= ids.length) {
      return;
    }

    const orderedIds = [...ids];
    [orderedIds[currentIndex], orderedIds[nextIndex]] = [orderedIds[nextIndex], orderedIds[currentIndex]];

    this.reordering = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post<MenuSaveResponse>(this.menuManagerUrl, {
      action: 'reorder',
      kind,
      parent_id: parentId,
      ordered_ids: orderedIds,
    }).subscribe({
      next: (response) => {
        this.reordering = false;
        const selection = this.currentSelection() ?? { kind, id: itemId };
        if (response.tree) {
          this.applyTreeResponse(response.tree, selection);
          this.loadProductionStatus();
        }
        else {
          this.loadMenuData(selection);
        }
        this.successMessage = response?.message || 'Menu order updated.';
      },
      error: (error) => {
        this.reordering = false;
        this.errorMessage = error?.error?.message || 'Unable to reorder menu items.';
        this.toastr.error(this.errorMessage);
      }
    });
  }

  private canMove(siblings: Array<MenuItem | SubMenuItem | SubSubMenuItem>, itemId: number, direction: -1 | 1, kind: MenuKind): boolean {
    const ids = siblings.map((item) => this.idForKind(kind, item)).filter((id): id is number => id !== null);
    const index = ids.indexOf(itemId);
    const nextIndex = index + direction;
    return index >= 0 && nextIndex >= 0 && nextIndex < ids.length && !this.reordering;
  }

  private productionSummaryValue(key: ProductionSummaryKey): number {
    const value = this.productionCompare?.summary?.[key];
    return Number.isFinite(Number(value)) ? Number(value) : 0;
  }

  private currentSelection(): MenuSelection | null {
    return this.editorMode === 'edit' && this.selectedId !== null
      ? { kind: this.selectedKind, id: this.selectedId }
      : null;
  }

  private selectionFromSavedItem(item?: Record<string, unknown>): MenuSelection | null {
    if (!item) {
      return null;
    }

    const id = this.idFromRawItem(this.selectedKind, item);
    return id === null ? null : { kind: this.selectedKind, id };
  }

  private selectFirstAvailable(): void {
    const firstMenu = this.menus[0];

    if (firstMenu) {
      this.selectExisting('menu', firstMenu.ams_side_menu_id, false);
      return;
    }

    this.beginAddMenu();
  }

  private findItem(kind: MenuKind, id: number): MenuItem | SubMenuItem | SubSubMenuItem | null {
    if (kind === 'menu') {
      return this.menus.find((menu) => menu.ams_side_menu_id === id) ?? null;
    }

    if (kind === 'subMenu') {
      return this.subMenus.find((subMenu) => subMenu.ams_side_sub_menu_id === id) ?? null;
    }

    return this.subSubMenus.find((subSubMenu) => subSubMenu.ams_side_sub_sub_menu_id === id) ?? null;
  }

  private idForKind(kind: MenuKind, item: MenuItem | SubMenuItem | SubSubMenuItem): number | null {
    if (kind === 'menu') {
      return (item as MenuItem).ams_side_menu_id ?? null;
    }

    if (kind === 'subMenu') {
      return (item as SubMenuItem).ams_side_sub_menu_id ?? null;
    }

    return (item as SubSubMenuItem).ams_side_sub_sub_menu_id ?? null;
  }

  private idFromRawItem(kind: MenuKind, item: Record<string, unknown>): number | null {
    if (kind === 'menu') {
      return this.optionalNumber(item['ams_side_menu_id']);
    }

    if (kind === 'subMenu') {
      return this.optionalNumber(item['ams_side_sub_menu_id']);
    }

    return this.optionalNumber(item['ams_side_sub_sub_menu_id']);
  }

  private preferredParentMenuId(): number | null {
    if (this.selectedKind === 'menu' && this.selectedId !== null) {
      return this.selectedId;
    }

    if (this.selectedKind === 'subMenu' && this.selectedId !== null) {
      return (this.findItem('subMenu', this.selectedId) as SubMenuItem | null)?.ams_side_menu_id ?? null;
    }

    return this.menus[0]?.ams_side_menu_id ?? null;
  }

  private preferredParentSubMenuId(): number | null {
    if (this.selectedKind === 'subMenu' && this.selectedId !== null) {
      return this.selectedId;
    }

    if (this.selectedKind === 'subSubMenu' && this.selectedId !== null) {
      return (this.findItem('subSubMenu', this.selectedId) as SubSubMenuItem | null)?.ams_side_sub_menu_id ?? null;
    }

    return this.subMenus[0]?.ams_side_sub_menu_id ?? null;
  }

  private normalizeMenu(raw: any): MenuItem {
    const children = (raw?.children ?? []).map((item: any) => this.normalizeSubMenu(item));

    return {
      ams_side_menu_id: this.optionalNumber(raw?.ams_side_menu_id) ?? 0,
      ams_side_menu_text: String(raw?.ams_side_menu_text ?? ''),
      ams_side_menu_order: this.optionalNumber(raw?.ams_side_menu_order),
      ams_side_menu_description: this.nullableString(raw?.ams_side_menu_description),
      access_roles: this.optionalNumber(raw?.access_roles) ?? 0,
      children,
    };
  }

  private normalizeSubMenu(raw: any): SubMenuItem {
    const children = (raw?.children ?? []).map((item: any) => this.normalizeSubSubMenu(item));

    return {
      ams_side_sub_menu_id: this.optionalNumber(raw?.ams_side_sub_menu_id) ?? 0,
      ams_side_menu_id: this.optionalNumber(raw?.ams_side_menu_id),
      ams_side_sub_menu_text: String(raw?.ams_side_sub_menu_text ?? ''),
      ams_side_sub_menu_page: this.nullableString(raw?.ams_side_sub_menu_page),
      ams_side_sub_menu_icon: this.nullableString(raw?.ams_side_sub_menu_icon),
      ams_side_sub_menu_order: this.optionalNumber(raw?.ams_side_sub_menu_order),
      ams_side_sub_sub_menu: this.toBoolean(raw?.ams_side_sub_sub_menu),
      ams_side_sub_menu_description: this.nullableString(raw?.ams_side_sub_menu_description),
      access_roles: this.optionalNumber(raw?.access_roles) ?? 0,
      menu_parent: this.nullableString(raw?.menu_parent),
      child_count: this.optionalNumber(raw?.child_count) ?? children.length,
      children,
    };
  }

  private normalizeSubSubMenu(raw: any): SubSubMenuItem {
    return {
      ams_side_sub_sub_menu_id: this.optionalNumber(raw?.ams_side_sub_sub_menu_id) ?? 0,
      ams_side_sub_menu_id: this.optionalNumber(raw?.ams_side_sub_menu_id),
      ams_side_sub_sub_menu_text: String(raw?.ams_side_sub_sub_menu_text ?? ''),
      ams_side_sub_sub_menu_page: this.nullableString(raw?.ams_side_sub_sub_menu_page),
      ams_side_sub_sub_menu_icon: this.nullableString(raw?.ams_side_sub_sub_menu_icon),
      ams_side_sub_sub_menu_order: this.optionalNumber(raw?.ams_side_sub_sub_menu_order),
      ams_side_sub_sub_menu_description: this.nullableString(raw?.ams_side_sub_sub_menu_description),
      access_roles: this.optionalNumber(raw?.access_roles) ?? 0,
      sub_menu_parent: this.nullableString(raw?.sub_menu_parent),
    };
  }

  private menuHaystack(menu: MenuItem): string {
    return [menu.ams_side_menu_text, menu.ams_side_menu_description, menu.ams_side_menu_order].join(' ').toLowerCase();
  }

  private subMenuHaystack(subMenu: SubMenuItem): string {
    return [
      subMenu.ams_side_sub_menu_text,
      subMenu.ams_side_sub_menu_page,
      subMenu.ams_side_sub_menu_icon,
      subMenu.ams_side_sub_menu_description,
      subMenu.menu_parent,
      subMenu.ams_side_sub_menu_order,
    ].join(' ').toLowerCase();
  }

  private subSubMenuHaystack(subSubMenu: SubSubMenuItem): string {
    return [
      subSubMenu.ams_side_sub_sub_menu_text,
      subSubMenu.ams_side_sub_sub_menu_page,
      subSubMenu.ams_side_sub_sub_menu_icon,
      subSubMenu.ams_side_sub_sub_menu_description,
      subSubMenu.sub_menu_parent,
      subSubMenu.ams_side_sub_sub_menu_order,
    ].join(' ').toLowerCase();
  }

  private cleanText(value: unknown): string {
    return String(value ?? '').trim();
  }

  private nullableString(value: unknown): string | null {
    const text = this.cleanText(value);
    return text ? text : null;
  }

  private hasText(value: unknown): boolean {
    return this.cleanText(value).length > 0;
  }

  private optionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private toBoolean(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 't' || value === 'true';
  }
}
